// src/components/HeroSection.jsx
// PERFORMANCE CHANGES:
// 1. Hero background is a real <img> with fetchPriority="high" + loading="eager"
//    so the browser's preload scanner picks it up immediately (same as the
//    <link rel="preload"> in index.html — belt and suspenders).
// 2. Floating SVG decorations are wrapped in a lazy Suspense boundary so they
//    don't block the initial paint.
// 3. OrganiShape blobs are deferred with a tiny setTimeout so they render
//    after the LCP image is painted.

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import OrganiShape from './OrganiShape.jsx';
import FloatingIllustration from './FloatingIllustration.jsx';

// LCP hero image — same URL as the <link rel="preload"> in index.html
const HERO_BG =
  'https://hubluonline.co.za/images/photo-1583828883089-49a7ff344649.jpg';

export default function HeroSection() {
  // Defer decorative elements until after first paint
  const [showDecorations, setShowDecorations] = useState(false);

  useEffect(() => {
    // ~300ms after mount — well after the LCP image has started loading
    const t = setTimeout(() => setShowDecorations(true), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[hsl(var(--warm-sand))] to-[hsl(var(--cream-white))]">

      {/* ── LCP Image ─────────────────────────────────────────────────
          Rendered as a real <img> so the browser preload scanner and
          the <link rel="preload"> in index.html both find it.
          object-cover + absolute fill replicates the old CSS bg approach.
          fetchPriority="high" tells the browser this IS the LCP element.
      ──────────────────────────────────────────────────────────────── */}
      <img
        src={HERO_BG}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        loading="eager"
        decoding="sync"
        width="1600"
        height="900"
        className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none"
      />

      {/* ── Decorative blobs — deferred so they don't block LCP ───── */}
      {showDecorations && (
        <>
          <OrganiShape color="mint" className="top-10 -right-20 w-[500px] h-[500px]" opacity={0.4} />
          <OrganiShape color="coral" className="-bottom-32 -left-20 w-[600px] h-[600px]" opacity={0.2} delay={1} />
          <OrganiShape color="sky" className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]" opacity={0.15} delay={2} />

          <FloatingIllustration type="block" className="top-24 left-[15%]" floatDuration={3} />
          <FloatingIllustration type="car" className="bottom-40 right-[15%]" animationType="rotate" delay={1} />
          <FloatingIllustration type="house" className="top-32 right-[25%]" animationType="sway" delay={0.5} />
          <FloatingIllustration type="pet" className="bottom-32 left-[25%]" floatDuration={4} delay={1.5} />
        </>
      )}

      {/* ── Hero text content ──────────────────────────────────────── */}
      <div className="relative z-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">

        {/* h1 — no animation delay so text is immediately visible (FCP) */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="text-5xl md:text-6xl lg:text-7xl font-bold text-[hsl(var(--charcoal))] leading-[1.1] tracking-tight mb-6"
        >
          Premium Wooden Toys &<br className="hidden md:block" /> Pet Products
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.15 }}
          className="text-lg md:text-xl text-[hsl(var(--charcoal))]/80 max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Discover beautifully crafted, sustainable products designed to bring
          joy and enrichment to your family and pets.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut', delay: 0.25 }}
        >
          <Link
            to="/shop"
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-full bg-[hsl(var(--primary))] text-white shadow-[0_0_20px_rgba(255,87,34,0.4)] hover:shadow-[0_0_30px_rgba(255,87,34,0.6)] hover:bg-[hsl(16,100%,45%)] transition-all duration-300 relative overflow-hidden group"
          >
            <span className="relative z-10">Shop Collection</span>
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}